import React from "react";
import Isotipo from "@/components/Isotipo";
import MockupShell from "./MockupShell";
import { NAV, HERO, WORLDS, WORK, METRICS, NEWS, CONTACT } from "./data";

const mont = { fontFamily: "Montserrat, sans-serif" };
const body = { fontFamily: "Manrope, sans-serif" };

const DARK = "#171726";
const LIGHT = "#F8F8F6";
const GREEN = "#26775e";
const GOLD = "#edaa51";

const THEME = { bg: DARK, text: "#F4F4F5", accent: GOLD, sub: GREEN };

const V3 = () => {
  return (
    <MockupShell theme={THEME}>
    <div data-testid="mockup-v3" className="min-h-screen" style={{ backgroundColor: DARK, ...body }}>
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl" style={{ backgroundColor: "rgba(23,23,38,0.72)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between" style={{ color: "#F4F4F5" }}>
          <div className="flex items-center gap-2.5">
            <Isotipo className="w-5 h-5" fill={GOLD} />
            <span className="text-base font-extrabold tracking-tight" style={mont}>GRABARTE</span>
          </div>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <span key={n} className="text-[12px] font-medium uppercase tracking-[0.12em] cursor-default transition-colors hover:text-[#edaa51]" style={{ ...mont, color: "#A1A1AA" }}>{n}</span>
            ))}
          </nav>
          <div className="flex items-center gap-5">
            <span className="hidden sm:inline text-[11px] font-bold tracking-widest" style={{ ...mont, color: "#71717A" }}><span style={{ color: GOLD }}>ES</span> / EN</span>
            <span className="inline-flex items-center rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] cursor-default" style={{ ...mont, backgroundColor: GOLD, color: DARK }}>Hablemos</span>
          </div>
        </div>
      </header>

      {/* Hero (dark) */}
      <section className="relative" style={{ backgroundColor: DARK, color: "#F4F4F5" }}>
        <div className="max-w-6xl mx-auto px-6 pt-40 pb-28">
          <p className="text-xs uppercase tracking-[0.3em] mb-8" style={{ ...mont, color: GOLD }}>{HERO.eyebrow}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02] max-w-3xl" style={mont}>{HERO.title}</h1>
          <p className="mt-7 text-lg leading-relaxed max-w-xl" style={{ color: "#A1A1AA" }}>{HERO.sub}</p>
        </div>
      </section>

      {/* Audiovisual world (dark) */}
      <section className="py-24" style={{ backgroundColor: DARK, color: "#F4F4F5", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] mb-4" style={{ ...mont, color: GOLD }}>{WORLDS[0].label}</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={mont}>{WORLDS[0].line}</h2>
            <p className="mt-4 text-base leading-relaxed max-w-md" style={{ color: "#A1A1AA" }}>{WORLDS[0].desc}</p>
          </div>
          <div className="overflow-hidden rounded-md aspect-[16/11]">
            <img src={WORLDS[0].image} alt={WORLDS[0].label} className="w-full h-full object-cover" style={{ filter: "grayscale(0.2)" }} />
          </div>
        </div>
      </section>

      {/* Marketing world (light) */}
      <section className="py-24" style={{ backgroundColor: LIGHT, color: DARK }}>
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div className="overflow-hidden rounded-md aspect-[16/11] order-2 md:order-1">
            <img src={WORLDS[1].image} alt={WORLDS[1].label} className="w-full h-full object-cover" />
          </div>
          <div className="order-1 md:order-2">
            <p className="text-[11px] uppercase tracking-[0.25em] mb-4" style={{ ...mont, color: GREEN, fontWeight: 700 }}>{WORLDS[1].label}</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={mont}>{WORLDS[1].line}</h2>
            <p className="mt-4 text-base leading-relaxed max-w-md" style={{ color: "#52525B" }}>{WORLDS[1].desc}</p>
          </div>
        </div>
      </section>

      {/* Work (light continues) */}
      <section className="py-24" style={{ backgroundColor: LIGHT, color: DARK, borderTop: "1px solid rgba(23,23,38,0.1)" }}>
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-12" style={mont}>Proyectos seleccionados</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {WORK.map((p) => (
              <div key={p.title} className="group">
                <div className="overflow-hidden rounded-md aspect-[4/3] mb-4">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <p className="text-[10px] uppercase tracking-[0.22em]" style={{ ...mont, color: GREEN, fontWeight: 700 }}>{p.kicker}</p>
                <h3 className="mt-1.5 text-base font-semibold" style={mont}>{p.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Metrics (dark) */}
      <section className="py-20" style={{ backgroundColor: DARK, color: "#F4F4F5" }}>
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6">
          {METRICS.map((m) => (
            <div key={m.label}>
              <p className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ ...mont, color: GOLD }}>{m.value}<span style={{ color: "#F4F4F5" }}>{m.suffix}</span></p>
              <p className="mt-2 text-xs leading-snug" style={{ color: "#A1A1AA" }}>{m.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* News (light) */}
      <section className="py-24" style={{ backgroundColor: LIGHT, color: DARK }}>
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-10" style={mont}>Actualidad</h2>
          {NEWS.map((n) => (
            <div key={n.title} className="py-6 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-8" style={{ borderTop: "1px solid rgba(23,23,38,0.12)" }}>
              <span className="text-[11px] uppercase tracking-[0.2em] shrink-0 w-40" style={{ ...mont, color: GREEN, fontWeight: 700 }}>{n.meta}</span>
              <span className="text-base sm:text-lg">{n.title}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Contact (dark) */}
      <section className="py-24" style={{ backgroundColor: DARK, color: "#F4F4F5" }}>
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] mb-6" style={{ ...mont, color: GOLD }}>Contacto</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-md" style={mont}>Hablemos de tu proyecto</h2>
          </div>
          <div className="space-y-5 text-lg">
            <p><span className="block text-[11px] uppercase tracking-[0.2em] mb-1" style={{ ...mont, color: "#71717A" }}>Email</span>{CONTACT.email}</p>
            <p><span className="block text-[11px] uppercase tracking-[0.2em] mb-1" style={{ ...mont, color: "#71717A" }}>Teléfono</span>{CONTACT.phone}</p>
            <p><span className="block text-[11px] uppercase tracking-[0.2em] mb-1" style={{ ...mont, color: "#71717A" }}>Dirección</span>{CONTACT.address}</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10" style={{ backgroundColor: DARK, color: "#F4F4F5", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-2"><Isotipo className="w-4 h-4" fill={GOLD} /><span className="text-sm font-bold tracking-tight" style={mont}>GRABARTE</span></div>
          <span className="text-xs" style={{ color: "#71717A" }}>© 2026 Grabarte · Madrid</span>
        </div>
      </footer>

    </div>
    </MockupShell>
  );
};

export default V3;
